import { useFusionAuth } from "@fusionauth/react-sdk";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveIcon from "@mui/icons-material/Save";
import {
  Badge,
  Button,
  Divider,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import {
  type GridInitialState,
  useGridApiContext,
} from "@mui/x-data-grid-premium";
import { isEqualWith } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { PatientContext } from "../../contexts/PatientContext.tsx";
import { ProjectContext } from "../../contexts/ProjectContext.tsx";
import theme from "../../styles/theme.ts";
import CloseArrowTooltip from "../CloseArrowTooltip.tsx";
import { TableToolbarButton } from "./TableToolbarButton.tsx";
import {
  equalsCustomizer,
  loadScopedViews,
  REQUIRED_GLOBAL_VIEW,
  type StateView,
  viewStateReducer,
} from "./ToolbarViewMenuUtils.tsx";

function ViewListItem(props: {
  view: StateView;
  viewId: string;
  selected: boolean;
  isLocked?: boolean;
  onDelete: (viewId: string) => void;
  onRename: (viewId: string, label: string) => void;
  onSelect: (viewId: string) => void;
}) {
  const {
    view,
    viewId,
    selected,
    isLocked = false,
    onDelete,
    onRename,
    onSelect,
  } = props;
  const [isEditing, setIsEditing] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [draftLabel, setDraftLabel] = useState(view.label);

  useEffect(() => {
    setDraftLabel(view.label);
  }, [view.label]);

  const commitRename = () => {
    const trimmed = draftLabel.trim();
    setIsEditing(false);
    if (!trimmed || trimmed === view.label) {
      setDraftLabel(view.label);
      return;
    }
    onRename(viewId, trimmed);
  };

  const cancelRename = () => {
    setDraftLabel(view.label);
    setIsEditing(false);
  };

  return (
    <MenuItem
      key={viewId}
      selected={selected}
      onClick={() => !isEditing && !isConfirmingDelete && onSelect(viewId)}
    >
      {isEditing ? (
        <TextField
          autoFocus
          size="small"
          value={draftLabel}
          onChange={(event) => setDraftLabel(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commitRename();
            }
            if (event.key === "Escape") {
              event.preventDefault();
              cancelRename();
            }
          }}
        />
      ) : (
        <Typography>{view.label}</Typography>
      )}
      {!isLocked &&
        (isConfirmingDelete ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              paddingLeft: 1,
            }}
          >
            <Button
              size="small"
              variant="text"
              onClick={(event) => {
                event.stopPropagation();
                setIsConfirmingDelete(false);
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              color="error"
              variant="contained"
              onClick={(event) => {
                event.stopPropagation();
                setIsConfirmingDelete(false);
                onDelete(viewId);
              }}
            >
              Delete
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              paddingLeft: 1,
            }}
          >
            {isEditing ? (
              <IconButton
                edge="end"
                aria-label="rename"
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  setIsConfirmingDelete(false);
                  setIsEditing(false);
                  commitRename();
                }}
              >
                <SaveIcon fontSize="small" />
              </IconButton>
            ) : (
              <IconButton
                edge="end"
                aria-label="rename"
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  setIsConfirmingDelete(false);
                  setIsEditing(true);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton
              edge="end"
              aria-label="delete"
              size="small"
              disabled={isEditing}
              onClick={(event) => {
                event.stopPropagation();
                setIsEditing(false);
                setIsConfirmingDelete(true);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
    </MenuItem>
  );
}

export default function ToolbarViewMenu({
  tableId,
  onViewStateReady,
}: {
  tableId: string;
  onViewStateReady?: () => void;
}) {
  const apiRef = useGridApiContext();
  const appliedBaselineRef = useRef<GridInitialState | null>(null);
  const onViewStateReadyRef = useRef(onViewStateReady);
  onViewStateReadyRef.current = onViewStateReady;
  const [defaultGridState] = useState<GridInitialState>(
    apiRef.current.exportState(),
  );

  const [state, dispatch] = useReducer(viewStateReducer, {
    views: {},
    activeViewId: null,
  });
  const { id: projectId } = useContext(ProjectContext) || {};
  const { forceTableUpdate } = useContext(PatientContext) || {};
  const { userInfo } = useFusionAuth();
  const userId = userInfo?.sub || "";

  const [isViewDirty, setIsViewDirty] = useState(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);
  const [showNewViewFields, setShowNewViewFields] = useState(false);
  const [newViewLabel, setNewViewLabel] = useState("");
  const [newViewScope, setNewViewScope] = useState<"global" | "project">(
    projectId ? "project" : "global",
  );
  const activeViewLabel = state.activeViewId
    ? (state.views[state.activeViewId]?.label ?? null)
    : null;
  const subtitleViewLabel = activeViewLabel ?? REQUIRED_GLOBAL_VIEW.label;

  const orderedFilteredViewEntries = useMemo(() => {
    const filteredViewEntries = Object.entries(state.views);

    const defaultEntry = filteredViewEntries.find(
      ([viewId]) => viewId === REQUIRED_GLOBAL_VIEW.id,
    );
    const nonDefaultEntries = filteredViewEntries.filter(
      ([viewId]) => viewId !== REQUIRED_GLOBAL_VIEW.id,
    );

    return defaultEntry
      ? [defaultEntry, ...nonDefaultEntries]
      : nonDefaultEntries;
  }, [state.views]);

  const globalViewEntries = useMemo(
    () =>
      orderedFilteredViewEntries.filter(([, view]) => view.scope === "global"),
    [orderedFilteredViewEntries],
  );

  const projectViewEntries = useMemo(
    () =>
      orderedFilteredViewEntries.filter(([, view]) => view.scope === "project"),
    [orderedFilteredViewEntries],
  );

  const markCleanFromCurrentState = useCallback(() => {
    appliedBaselineRef.current = apiRef.current.exportState();
    setIsViewDirty(false);
  }, [apiRef]);

  const restoreState = useCallback(
    (viewId: string, view: StateView) => {
      try {
        apiRef.current.restoreState(view.value);
      } catch {
        // The table still maintains the broken state even though the restoreState call failed
        // Restore the initial state so the table can load properly
        apiRef.current.restoreState(defaultGridState);
        // If the JSON is invalid or the state is incompatible (e.g. column type changed),
        // Remove invalid state object from storage
        dispatch({
          type: "delete",
          userId,
          tableId,
          projectId,
          viewId,
          scope: view.scope,
        });
      }
    },
    [apiRef, defaultGridState, projectId, tableId, userId],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setTimeout(() => {
      setShowNewViewFields(false);
      setNewViewLabel("");
      setNewViewScope(projectId ? "project" : "global");
    }, theme.transitions.duration.leavingScreen);
  }, [projectId]);

  const handleDeleteView = useCallback(
    (viewId: string) => {
      if (viewId === REQUIRED_GLOBAL_VIEW.id) {
        return;
      }

      const view = state.views[viewId];
      if (!view) {
        return;
      }

      dispatch({
        type: "delete",
        viewId,
        userId,
        tableId,
        scope: view.scope,
        projectId,
      });
      const defaultView = state.views[REQUIRED_GLOBAL_VIEW.id];
      appliedBaselineRef.current = defaultView.value;
      restoreState(viewId, defaultView);
      handleClose();
    },
    [handleClose, projectId, restoreState, state.views, tableId, userId],
  );

  const handleRenameView = useCallback(
    (viewId: string, label: string) => {
      if (viewId === REQUIRED_GLOBAL_VIEW.id) {
        return;
      }

      const view = state.views[viewId];
      if (!view) {
        return;
      }

      const trimmed = label.trim();
      if (!trimmed || trimmed === view.label) {
        return;
      }

      const duplicate = Object.entries(state.views).some(
        ([id, v]) => id !== viewId && v.label === trimmed,
      );
      if (duplicate) {
        return;
      }

      dispatch({
        type: "rename",
        viewId,
        label: trimmed,
        userId,
        tableId,
        scope: view.scope,
        projectId,
      });
      handleClose();
    },
    [state.views, userId, tableId, projectId, handleClose],
  );

  const handleSetActiveView = useCallback(
    (viewId: string) => {
      const view = state.views[viewId];
      if (!view) {
        return;
      }

      dispatch({
        type: "active",
        viewId,
        userId,
        tableId,
        projectId,
      });
      appliedBaselineRef.current = view.value;
      restoreState(viewId, view);

      handleClose();
    },
    [handleClose, projectId, restoreState, state.views, tableId, userId],
  );

  const handleSaveCurrent = () => {
    if (!state.activeViewId || state.activeViewId === REQUIRED_GLOBAL_VIEW.id) {
      return;
    }
    const view = state.views[state.activeViewId];
    if (!view) {
      return;
    }
    const viewState = apiRef.current.exportState();

    dispatch({
      type: "update",
      viewId: state.activeViewId,
      userId,
      tableId,
      scope: view.scope,
      projectId,
      value: viewState,
    });
    markCleanFromCurrentState();
    handleClose();
  };

  const handleSaveNew = () => {
    const viewId = `${newViewScope}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const viewState = apiRef.current.exportState();
    const trimmed = newViewLabel.trim();
    if (!trimmed) {
      return;
    }

    const duplicate = Object.entries(state.views).some(
      ([id, v]) => id !== viewId && v.label === trimmed,
    );
    if (duplicate) {
      return;
    }

    const view: StateView = {
      label: trimmed,
      value: viewState,
      scope: newViewScope,
    };

    dispatch({
      type: "create",
      viewId,
      scope: view.scope,
      userId,
      tableId,
      projectId,
      view,
    });

    markCleanFromCurrentState();
    handleClose();
  };

  const isNewViewLabelValid = useMemo(() => {
    if (newViewLabel.length === 0) {
      return false;
    }

    return Object.values(state.views).every(
      (view) => view.label !== newViewLabel,
    );
  }, [state.views, newViewLabel]);

  const syncDirtyState = useCallback(() => {
    const appliedBaseline = appliedBaselineRef.current;
    const activeView = state.activeViewId
      ? state.views[state.activeViewId]
      : null;
    const baselineState =
      appliedBaseline ?? activeView?.value ?? defaultGridState ?? {};
    const currentState = apiRef.current.exportState();
    const nextDirty = !isEqualWith(
      baselineState,
      currentState,
      equalsCustomizer,
    );
    setIsViewDirty((prev) => (prev === nextDirty ? prev : nextDirty));
  }, [apiRef, defaultGridState, state.activeViewId, state.views]);

  useEffect(() => {
    const hydrated = loadScopedViews(
      userId,
      tableId,
      defaultGridState,
      projectId,
    );
    const hasHydratedViews = Object.keys(hydrated.views).length > 0;
    const nextViews = hasHydratedViews ? hydrated.views : {};
    const nextActiveViewId =
      hydrated.activeViewId ?? Object.keys(nextViews)[0] ?? null;

    dispatch({
      type: "load",
      views: nextViews,
      activeViewId: nextActiveViewId,
    });

    if (nextActiveViewId && nextViews[nextActiveViewId]) {
      appliedBaselineRef.current = nextViews[nextActiveViewId].value;
      restoreState(nextActiveViewId, nextViews[nextActiveViewId]);
    }

    onViewStateReadyRef.current?.();
  }, [
    apiRef,
    projectId,
    defaultGridState,
    userId,
    tableId,
    forceTableUpdate,
    restoreState,
  ]);

  useEffect(() => {
    const unsubscribe = apiRef.current.subscribeEvent("stateChange", () => {
      syncDirtyState();
    });

    return () => {
      unsubscribe();
    };
  }, [apiRef, syncDirtyState]);

  return (
    <div>
      <CloseArrowTooltip
        title={activeViewLabel ?? ""}
        disableHoverListener={!activeViewLabel}
        enterDelay={600}
      >
        <TableToolbarButton
          id="custom-view-button"
          size="small"
          sx={{ width: 160 }}
          endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          <Stack alignItems={"center"} paddingRight={1} width={126}>
            <Typography
              variant="body2"
              component="div"
              sx={{ fontWeight: 600 }}
            >
              My Saved Views
            </Typography>
            <Typography
              variant="caption"
              component="div"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              overflow="hidden"
              width="inherit"
            >
              {subtitleViewLabel}
            </Typography>
          </Stack>
          <Badge variant="dot" color="warning" invisible={!isViewDirty} />
        </TableToolbarButton>
      </CloseArrowTooltip>
      <Menu
        id="custom-view-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          list: {
            "aria-labelledby": "custom-view-button",
            role: "listbox",
            sx: {
              width: 400,
            },
            dense: true,
          },
        }}
      >
        {globalViewEntries.length > 0 && (
          <ListSubheader>Global Views</ListSubheader>
        )}
        {globalViewEntries.map(([viewId, view]) => (
          <ViewListItem
            key={viewId}
            view={view}
            viewId={viewId}
            isLocked={viewId === REQUIRED_GLOBAL_VIEW.id}
            selected={viewId === state.activeViewId}
            onDelete={handleDeleteView}
            onRename={handleRenameView}
            onSelect={handleSetActiveView}
          />
        ))}
        {globalViewEntries.length > 0 && projectViewEntries.length > 0 && (
          <Divider sx={{ my: 0.5 }} />
        )}
        {projectViewEntries.length > 0 && (
          <ListSubheader>Project Views</ListSubheader>
        )}
        {projectViewEntries.map(([viewId, view]) => (
          <ViewListItem
            key={viewId}
            view={view}
            viewId={viewId}
            isLocked={viewId === REQUIRED_GLOBAL_VIEW.id}
            selected={viewId === state.activeViewId}
            onDelete={handleDeleteView}
            onRename={handleRenameView}
            onSelect={handleSetActiveView}
          />
        ))}
        {orderedFilteredViewEntries.length > 0 && <Divider sx={{ my: 0.5 }} />}
        <MenuItem
          key={"save-current-view"}
          disabled={
            activeViewLabel === REQUIRED_GLOBAL_VIEW.label || !isViewDirty
          }
          onClick={() => handleSaveCurrent()}
        >
          Save changes to current view
        </MenuItem>
        <MenuItem
          key={"save-new-view"}
          disabled={!isViewDirty}
          onClick={() => setShowNewViewFields(true)}
        >
          Save changes as a new reusable view
        </MenuItem>
        {showNewViewFields && (
          <Stack paddingY={1} paddingX={2} spacing={1}>
            <TextField
              required
              label="View Name"
              size="small"
              placeholder={"e.g. Filtered by name"}
              fullWidth
              value={newViewLabel}
              onChange={(event) => {
                setNewViewLabel(event.target.value);
              }}
              onKeyDown={(e) => {
                // Don't let MenuList intercept regular text input
                if (
                  e.key.length === 1 ||
                  e.key === "Backspace" ||
                  e.key === "Delete"
                ) {
                  e.stopPropagation();
                }
              }}
            />
            <ToggleButtonGroup
              value={newViewScope}
              exclusive
              disabled={!projectId}
              onChange={(_event, value) => {
                if (value === "project" || value === "global") {
                  setNewViewScope(value);
                }
              }}
              size="small"
              color="primary"
              aria-label="ViewScopeGroup"
            >
              <ToggleButton value={"project"}>This Project</ToggleButton>
              <ToggleButton value={"global"}>Global</ToggleButton>
            </ToggleButtonGroup>
            <Stack direction="row" spacing={1} justifyContent="end">
              <Button variant="outlined" size="small" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                disabled={!isViewDirty || !isNewViewLabelValid}
                onClick={handleSaveNew}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        )}
      </Menu>
    </div>
  );
}
